import { useEffect, useState, useRef } from 'react'
import { Container, Divider, Paper, Typography, makeStyles } from '@material-ui/core'
import TemporaryPasswordForm from '../../component/um/researcher/TemporaryPasswordForm'
import useResearcherHook from '../../component/web/shareLogic/useResearcherHook'
import { useHistory, useParams } from 'react-router-dom'
import useTranslate from '../../i18n/useTranslate'
import { encryption } from '../../utilities/encryption'
import useLoading from '../../control/loading/useLoading'
import { RoutePath } from '../../router/routesConfig'
import Dialog, { DialogType } from '../../control/dialog/dialog'

type FormValues = {
    nameTh: string
    nameEn: string
    email: string
    phoneNumber: string
    password: string
    confirmPassword: string
    startDate: Date
    endDate: Date
}

type DialogRef = { openDialog?: () => void; closeDialog?: () => void }

const AddUserForm = () => {
    const classes = useStyles()
    const translate = useTranslate()
    const loadingHook = useLoading()
    const history = useHistory()

    const { getResearcherById, updateTempPassword } = useResearcherHook()
    const { id: userId } = useParams<{ id: string }>()
    const [initialValues, setInitialValues] = useState<Partial<FormValues>>({})

    const successDialogRef = useRef<DialogRef | null>(null)
    const [successConfig, setSuccessConfig] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm'>>()

    const goList = () => history.push({ pathname: RoutePath.manageResearcher })

    const bindSuccessRef = (func: Record<string, Function>) => {
        successDialogRef.current = func as unknown as DialogRef
    }

    const notifySuccess = () => {
        setSuccessConfig({
            title: translate.get('ALERT.TITLE.NOTIFICATION'),
            content: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
            type: 'alert-message',
            onClickConfirm: () => {
                successDialogRef.current?.closeDialog?.()
                goList()
            },
        })
        successDialogRef.current?.openDialog?.()
    }

    const fetchResearcherData = async () => {
        if (!userId) return
        loadingHook.loadingOpen()
        try {
            const data = await getResearcherById(Number(userId))
            setInitialValues({
                nameTh: data.name_th,
                nameEn: data.name_en,
                email: data.email,
                phoneNumber: data.phone_number,
                startDate: new Date(),
                endDate: (() => {
                    const d = new Date()
                    d.setDate(d.getDate() + 1)
                    return d
                })(),
            })
        } finally {
            loadingHook.loadingClose()
        }
    }

    useEffect(() => {
        fetchResearcherData()
    }, [userId])

    const onSubmit = async (values: FormValues) => {
        loadingHook.loadingOpen()
        try {
            await updateTempPassword(Number(userId), {
                password: encryption(values.password),
                startDate: values.startDate?.toISOString(),
                endDate: values.endDate?.toISOString(),
            })
            await notifySuccess()
        } finally {
            loadingHook.loadingClose()
        }
    }

    return (
        <Container>
            <Typography variant="h5" gutterBottom style={{ marginBottom: 16, fontWeight: 700 }}>
                {translate.get('UM.EDIT_RESEARCHER.HEADER')}
            </Typography>

            <Paper className={classes.root}>
                <Typography variant="h6" className={classes.sectionTitle}>
                    {translate.get('UM.EDIT_RESEARCHER.PERSONAL_DATA')}
                </Typography>
                <Divider />
                <TemporaryPasswordForm initialValues={initialValues} onSubmit={onSubmit} />
            </Paper>

            <Dialog
                onRef={bindSuccessRef}
                {...successConfig}
                onAfterClose={() => history.push({ pathname: RoutePath.manageResearcher })}
            />
        </Container>
    )
}

const useStyles = makeStyles((theme) => ({
    root: {
        borderRadius: 12,
    },
    sectionTitle: {
        fontWeight: 600,
        padding: theme.spacing(3),
    },
}))

export default AddUserForm
